/**
 * E-Mail-Versand (v1.4.0) — Status und Test-Mail für den globalen Admin.
 * Die SMTP-Zugangsdaten stehen ausschließlich in der Server-.env
 * (siehe deployment/README-docker.md „E-Mail-Versand"), nicht in der App.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../store/auth';
import { ApiError } from '../lib/api';
import { companiesApi } from '../lib/companies';
import { hapticError, hapticSuccess } from '../lib/haptics';

export function MailStatusCard() {
  const token = useAuth((s) => s.token)!;
  const { data: status } = useQuery({ queryKey: ['mail-status'], queryFn: () => companiesApi.mailStatus(token) });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);

  async function test() {
    setBusy(true);
    setMessage(null);
    try {
      const r = await companiesApi.mailTest(token);
      if (r.result === 'sent') {
        hapticSuccess();
        setMessage({ text: `Test-Mail an ${r.to} verschickt — bitte Posteingang (und Spam-Ordner) prüfen.`, ok: true });
      } else if (r.result === 'not-configured') {
        setMessage({ text: 'Kein Mailserver eingerichtet (SMTP_* in der Server-.env).', ok: false });
      } else {
        hapticError();
        setMessage({ text: 'Versand fehlgeschlagen — Zugangsdaten/Port prüfen, Details im Backend-Log.', ok: false });
      }
    } catch (err) {
      hapticError();
      setMessage({ text: err instanceof ApiError ? err.message : 'Test fehlgeschlagen', ok: false });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-1">E-Mail-Versand</h3>
          {!status ? (
            <p className="text-sm text-slate-500">Lade…</p>
          ) : status.configured ? (
            <p className="text-sm text-slate-300">
              Eingerichtet · Absender <code className="font-mono text-xs">{status.from}</code>
            </p>
          ) : (
            <p className="text-sm text-amber-300">
              Nicht eingerichtet — Einladungen und „Passwort vergessen“ werden nicht verschickt (Links nur im
              Server-Log). Einrichtung: SMTP-Daten des Webhosters in die Server-.env eintragen.
            </p>
          )}
          {status && <p className="text-xs text-slate-500 mt-1">Links in Mails zeigen auf {status.appUrl}</p>}
        </div>
        <button onClick={() => void test()} disabled={busy} className="btn-ghost text-xs px-3 py-1.5 shrink-0">
          {busy ? '…' : 'Test-Mail senden'}
        </button>
      </div>
      {message && <p className={`text-sm mt-2 ${message.ok ? 'text-emerald-400' : 'text-amber-300'}`}>{message.text}</p>}
    </section>
  );
}
